/**
 * Form Orchestrator
 *
 * Loads form enhancement modules based on element presence.
 * Static imports ensure all modules bundle into single file.
 *
 * Features:
 * - Conditional initialization based on element presence
 * - Centralized cleanup management
 * - Single bundled output file
 *
 * Modules:
 * - honeypot: Spam prevention for forms
 * - range: Range slider with CSS custom properties
 * - test: Development mode form testing
 * - select: Custom select UI (handles states dependency internally)
 */

import config from '@config';
import { getSelector, type ModuleConfig } from '@utils';
import { init as honeypotInit } from './functions/honeypot.ts';
import { init as rangeInit } from './functions/range.ts';
import { init as testInit } from './functions/test.ts';
import { init as selectInit } from './functions/select/select.ts';

export async function init() {
  const moduleConfig = config.default.form;
  const cleanup = {
    destroyFunctions: [] as Array<() => void>,
  };

  // Initialize honeypot if form-handler exists
  const honeypotSelector = getSelector(moduleConfig.honeypot as ModuleConfig, 'form-handler');
  if (document.querySelector(honeypotSelector)) {
    try {
      const result = honeypotInit(moduleConfig.honeypot);
      if (result?.destroy) {
        cleanup.destroyFunctions.push(result.destroy);
      }
    } catch (error) {
      console.error('[form] Error initializing honeypot:', error);
    }
  }

  // Initialize range if wrapper exists
  const rangeSelector = getSelector(moduleConfig.range as ModuleConfig, 'wrapper');
  if (document.querySelector(rangeSelector)) {
    try {
      const result = rangeInit(moduleConfig.range as any);
      if (result?.destroy) {
        cleanup.destroyFunctions.push(result.destroy);
      }
    } catch (error) {
      console.error('[form] Error initializing range:', error);
    }
  }

  // Initialize test if wrapper exists
  const testSelector = getSelector(moduleConfig.test as ModuleConfig, 'wrapper');
  if (document.querySelector(testSelector)) {
    try {
      const result = testInit(moduleConfig.test);
      if (result?.destroy) {
        cleanup.destroyFunctions.push(result.destroy);
      }
    } catch (error) {
      console.error('[form] Error initializing test:', error);
    }
  }

  // Initialize select if wrapper exists
  const selectSelector = getSelector(moduleConfig.select as ModuleConfig, 'wrapper');
  if (document.querySelector(selectSelector)) {
    try {
      const result = await selectInit(moduleConfig.select);
      if (result?.destroy) {
        cleanup.destroyFunctions.push(result.destroy);
      }
    } catch (error) {
      console.error('[form] Error initializing select:', error);
    }
  }

  return {
    result: `form initialized (${cleanup.destroyFunctions.length} modules active)`,
    destroy: () => {
      cleanup.destroyFunctions.forEach((destroyFn) => {
        try {
          destroyFn();
        } catch (error) {
          console.error('[form] Error during cleanup:', error);
        }
      });
      cleanup.destroyFunctions.length = 0;
    },
  };
}
